import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, Typography, Box, IconButton, Chip,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not Equals' },
  { value: 'greaterThan', label: 'Greater Than' },
  { value: 'lessThan', label: 'Less Than' },
  { value: 'contains', label: 'Contains' },
  { value: 'isEmpty', label: 'Is Empty' },
  { value: 'isNotEmpty', label: 'Is Not Empty' },
];

const SCOPE_OPTIONS = [
  { value: 'row', label: 'Entire Row' },
  { value: 'cell', label: 'Cell Only' },
];

const PRESET_STYLES = [
  { label: 'Red Highlight', style: { backgroundColor: '#FFCDD2', color: '#C62828', fontWeight: 'bold' } },
  { label: 'Green Highlight', style: { backgroundColor: '#C8E6C9', color: '#2E7D32', fontWeight: 'bold' } },
  { label: 'Yellow Highlight', style: { backgroundColor: '#FFF9C4', color: '#F57F17' } },
  { label: 'Blue Highlight', style: { backgroundColor: '#BBDEFB', color: '#1565C0' } },
  { label: 'Bold Text', style: { fontWeight: 'bold' } },
  { label: 'Italic Text', style: { fontStyle: 'italic' } },
];

export default function ConditionalFormatDialog({ open, onClose, columns, rules, onSave }) {
  const [localRules, setLocalRules] = useState(rules || []);

  const handleAddRule = () => {
    setLocalRules(prev => [...prev, {
      id: `rule_${Date.now()}`,
      name: '',
      condition: { column: columns[0]?.key || '', operator: 'equals', value: '' },
      style: { backgroundColor: '#FFCDD2', color: '#C62828', fontWeight: 'bold' },
      scope: 'row',
    }]);
  };

  const handleUpdateRule = (idx, field, value) => {
    setLocalRules(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      if (field.startsWith('condition.')) {
        const key = field.split('.')[1];
        return { ...r, condition: { ...r.condition, [key]: value } };
      }
      return { ...r, [field]: value };
    }));
  };

  const handleDeleteRule = (idx) => {
    setLocalRules(prev => prev.filter((_, i) => i !== idx));
  };

  const handleApplyPreset = (idx, preset) => {
    setLocalRules(prev => prev.map((r, i) => i === idx ? { ...r, style: { ...preset.style } } : r));
  };

  const handleSave = () => {
    onSave(localRules);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Conditional Formatting Rules
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {localRules.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No rules yet. Add a rule to highlight rows or cells based on conditions.
          </Typography>
        )}

        {localRules.map((rule, idx) => (
          <Box key={rule.id} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <TextField
                size="small" label="Rule Name" value={rule.name}
                onChange={(e) => handleUpdateRule(idx, 'name', e.target.value)}
                sx={{ width: 200 }}
              />
              <IconButton size="small" color="error" onClick={() => handleDeleteRule(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select fullWidth size="small" label="Column"
                  value={rule.condition.column}
                  onChange={(e) => handleUpdateRule(idx, 'condition.column', e.target.value)}
                >
                  {columns.map(col => (
                    <MenuItem key={col.key} value={col.key}>{col.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select fullWidth size="small" label="Operator"
                  value={rule.condition.operator}
                  onChange={(e) => handleUpdateRule(idx, 'condition.operator', e.target.value)}
                >
                  {OPERATORS.map(op => (
                    <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {!['isEmpty', 'isNotEmpty'].includes(rule.condition.operator) && (
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth size="small" label="Value"
                    value={rule.condition.value}
                    onChange={(e) => handleUpdateRule(idx, 'condition.value', e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={6} sm={2}>
                <TextField
                  select fullWidth size="small" label="Scope"
                  value={rule.scope}
                  onChange={(e) => handleUpdateRule(idx, 'scope', e.target.value)}
                >
                  {SCOPE_OPTIONS.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Style Presets:</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {PRESET_STYLES.map((preset) => (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    size="small"
                    onClick={() => handleApplyPreset(idx, preset)}
                    sx={{ ...preset.style, cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </Box>

            {/* Preview */}
            <Box sx={{ mt: 1, p: 1, borderRadius: 0.5, ...rule.style, border: '1px dashed #ccc' }}>
              <Typography variant="caption">Preview: Sample cell text</Typography>
            </Box>
          </Box>
        ))}

        <Button variant="outlined" onClick={handleAddRule} sx={{ mt: 1 }}>
          Add Rule
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save Rules</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Evaluate whether a conditional formatting rule matches a row.
 */
export function evaluateCondition(condition, rowData) {
  const { column, operator, value } = condition;
  const cellValue = rowData[column];

  switch (operator) {
    case 'equals': {
      if (value === 'true') return cellValue === true;
      if (value === 'false') return cellValue === false;
      const numVal = parseFloat(value);
      if (!isNaN(numVal) && typeof cellValue === 'number') return cellValue === numVal;
      return String(cellValue || '').toLowerCase() === String(value).toLowerCase();
    }
    case 'notEquals': {
      if (value === 'true') return cellValue !== true;
      if (value === 'false') return cellValue !== false;
      return String(cellValue || '').toLowerCase() !== String(value).toLowerCase();
    }
    case 'greaterThan': {
      const num = parseFloat(cellValue);
      const target = parseFloat(value);
      return !isNaN(num) && !isNaN(target) && num > target;
    }
    case 'lessThan': {
      const num = parseFloat(cellValue);
      const target = parseFloat(value);
      return !isNaN(num) && !isNaN(target) && num < target;
    }
    case 'contains':
      return String(cellValue || '').toLowerCase().includes(String(value).toLowerCase());
    case 'isEmpty':
      return cellValue === null || cellValue === undefined || cellValue === '' || cellValue === 0;
    case 'isNotEmpty':
      return cellValue !== null && cellValue !== undefined && cellValue !== '' && cellValue !== 0;
    default:
      return false;
  }
}
